import { useNode } from '@craftjs/core';
import {
	SliderControl,
	ToggleButtonGroupControl
} from 'pages/PageEditor/settingsComponents';
import { generateOnChange } from 'pages/PageEditor/settingsComponents/settingsHelpers';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import {
	horizontalAlignmentOptions,
	spaceBetweenOptions,
	spacingTypeOptions,
	verticalAlignmentOptions
} from './editorContainerSettingsConstants';

const Spacing = () => {
	const {
		direction,
		justify,
		spaceBetween,
		align,
		actions: { setProp }
	} = useNode((node) => node.data.props);

	const [onJustifyChange, onSpaceBetweenChange, onAlignChange] = useMemo(
		() =>
			['justify', 'spaceBetween', 'align'].map((attr) =>
				generateOnChange(setProp, attr)
			),
		[setProp]
	);

	// we want to keep the last "manual" vertical alignment selection, so when switching from "auto spacing" to "manual spacing" we can reselect the last value
	const [lastVerticalAlignment, setLastVerticalAlignment] = useState('start');

	// we keep the previous direction so we can identify if it was changed
	const prevDirection = useRef(direction);

	const handleSpacingType = useCallback(
		(type: 'between' | 'manual') => {
			// if user switch back to "manual spacing" we want to reselect his last "manual spacing" selection
			onJustifyChange(type === 'between' ? type : lastVerticalAlignment);
		},
		[lastVerticalAlignment, onJustifyChange]
	);

	const isAutoSpacing = direction === 'row' && justify === 'between';
	const isRowDirection = direction === 'row';

	// when switching direction, we want to switch the alignment & justify values so the user alignment selection will take place also with the new direction
	useEffect(() => {
		if (direction !== prevDirection.current) {
			onAlignChange(justify === 'between' ? lastVerticalAlignment : justify);
			onJustifyChange(align);
			prevDirection.current = direction;
		}
	}, [
		align,
		direction,
		justify,
		lastVerticalAlignment,
		onAlignChange,
		onJustifyChange
	]);

	// we want to keep the last "manual" alignment when the user changes it
	useEffect(() => {
		if (isRowDirection && justify !== 'between') {
			setLastVerticalAlignment(justify);
		}
	}, [isRowDirection, justify]);

	return (
		<div>
			{isRowDirection && (
				<ToggleButtonGroupControl
					options={spacingTypeOptions}
					label={intl.tr('screenEditor.settings.container.spacingType')}
					value={isAutoSpacing ? 'between' : 'manual'}
					setValue={handleSpacingType as (value: string) => void}
				/>
			)}

			{!isAutoSpacing && (
				<SliderControl
					label={intl.tr('screenEditor.settings.container.spaceBetween')}
					value={spaceBetween}
					options={spaceBetweenOptions}
					setValue={onSpaceBetweenChange}
					marks
				/>
			)}

			{!isAutoSpacing && (
				<ToggleButtonGroupControl
					data-testid="horizontal-alignment"
					label={intl.tr('screenEditor.settings.alignment')}
					options={horizontalAlignmentOptions}
					value={isRowDirection ? justify : align}
					setValue={isRowDirection ? onJustifyChange : onAlignChange}
				/>
			)}

			{isRowDirection && (
				<ToggleButtonGroupControl
					data-testid="vertical-alignment"
					label={
						isAutoSpacing ? intl.tr('screenEditor.settings.alignment') : ''
					}
					options={verticalAlignmentOptions}
					value={isRowDirection ? align : justify}
					setValue={isRowDirection ? onAlignChange : onJustifyChange}
				/>
			)}
		</div>
	);
};

export { Spacing };
